import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { BRAND_NAME } from '@/utils/constants'

interface OfferHeaderProps {
  hasPromoBar?: boolean
}

export function OfferHeader({ hasPromoBar = false }: OfferHeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu when clicking outside or on a link
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (mobileMenuOpen && !target.closest('nav')) {
        setMobileMenuOpen(false)
      }
    }
    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [mobileMenuOpen])

  // Offer page specific navigation items
  // Check if we're on morocco-doctors page for French navigation
  const isMoroccoDoctors = location.pathname === '/morocco-doctors'
  const offerItems = isMoroccoDoctors
    ? [
      { label: 'Démo', href: '#demo' },
      { label: 'Installation', href: '#how-it-works' },
      { label: 'Tarifs', href: '#pricing' },
      { label: 'FAQ', href: '#faq' },
    ]
    : [
      { label: 'Features', href: '#features' },
      { label: 'Talk to Sofia', href: '#sofia-demo' },
      { label: 'How It Works', href: '#benefits' },
      { label: 'Pricing', href: '#contact-form-section' },
    ]

  const scrollToSection = (href: string) => {
    setMobileMenuOpen(false) // Close mobile menu when clicking a link
    if (href.startsWith('#')) {
      const sectionId = href.substring(1)
      const element = document.getElementById(sectionId)
      if (element) {
        const yOffset = -100
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }
  }

  const handleLogoClick = () => {
    // Scroll to top of the page instead of navigating away
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <nav
      className={`
        fixed top-2 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-16px)] sm:w-fit max-w-[calc(100vw-16px)] transition-all duration-500
        backdrop-blur-xl rounded-2xl px-4 sm:px-4 py-2.5 sm:py-2.5
      `}
      style={{
        position: 'fixed',
        top: hasPromoBar ? '48px' : '8px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        background: isMoroccoDoctors
          ? scrolled
            ? 'rgba(0, 0, 0, 0.95)'
            : 'rgba(0, 0, 0, 0.9)'
          : scrolled
            ? 'linear-gradient(135deg, rgba(10, 28, 53, 0.95) 0%, rgba(19, 45, 76, 0.9) 100%)'
            : 'linear-gradient(135deg, rgba(10, 28, 53, 0.85) 0%, rgba(19, 45, 76, 0.75) 100%)',
        border: isMoroccoDoctors
          ? scrolled
            ? '1px solid rgba(255, 255, 255, 0.2)'
            : '1px solid rgba(255, 255, 255, 0.15)'
          : scrolled
            ? '1px solid rgba(203, 161, 53, 0.3)'
            : '1px solid rgba(203, 161, 53, 0.2)',
        boxShadow: isMoroccoDoctors
          ? scrolled
            ? '0 8px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            : '0 4px 24px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
          : scrolled
            ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 40px rgba(203, 161, 53, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            : '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 30px rgba(203, 161, 53, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px) saturate(180%)',
      }}
    >
      <div className="flex items-center justify-between gap-4 w-full">
        {/* Logo */}
        <button
          onClick={handleLogoClick}
          className="flex items-center flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
          aria-label="Go to homepage"
        >
          <img
            src="/logo-fortuno.webp"
            alt={`${BRAND_NAME} Logo`}
            className="h-6 sm:h-7 w-auto"
            width={643}
            height={660}
            style={{
              objectFit: 'contain',
              imageRendering: 'crisp-edges',
            }}
          />
        </button>

        {/* Desktop Navigation Links */}
        <div className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {offerItems.map((item) => (
            <button
              key={item.label}
              onClick={() => scrollToSection(item.href)}
              className="px-3 py-1.5 rounded-lg font-medium transition-all duration-200 whitespace-nowrap hover:bg-white/5"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                letterSpacing: '-0.01em',
                color: 'rgba(255, 255, 255, 0.9)',
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Desktop CTA Button */}
        <button
          onClick={() => {
            if (isMoroccoDoctors) {
              const schedulerElement = document.getElementById('scheduler')
              if (schedulerElement) {
                const yOffset = -100
                const y = schedulerElement.getBoundingClientRect().top + window.pageYOffset + yOffset
                window.scrollTo({ top: y, behavior: 'smooth' })
              }
            } else {
              navigate('/discover#scheduler')
            }
          }}
          className="hidden md:flex items-center gap-1 px-3 py-1.5 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 flex-shrink-0 whitespace-nowrap"
          style={{
            background: isMoroccoDoctors
              ? '#ffffff'
              : 'linear-gradient(135deg, #CBA135 0%, #E6C670 100%)',
            color: isMoroccoDoctors ? '#000000' : '#0A1C35',
            fontFamily: 'Playfair Display, Georgia, "Times New Roman", serif',
            fontWeight: 600,
            fontSize: '13px',
            boxShadow: isMoroccoDoctors
              ? '0 4px 20px rgba(0, 0, 0, 0.2), 0 0 30px rgba(0, 0, 0, 0.1)'
              : '0 4px 20px rgba(203, 161, 53, 0.5), 0 0 30px rgba(203, 161, 53, 0.2)',
          }}
          onMouseEnter={(e) => {
            if (isMoroccoDoctors) {
              e.currentTarget.style.boxShadow = '0 6px 28px rgba(0, 0, 0, 0.3), 0 0 40px rgba(0, 0, 0, 0.15)'
            } else {
              e.currentTarget.style.boxShadow = '0 6px 28px rgba(203, 161, 53, 0.7), 0 0 40px rgba(203, 161, 53, 0.3)'
            }
          }}
          onMouseLeave={(e) => {
            if (isMoroccoDoctors) {
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2), 0 0 30px rgba(0, 0, 0, 0.1)'
            } else {
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(203, 161, 53, 0.5), 0 0 30px rgba(203, 161, 53, 0.2)'
            }
          }}
        >
          {isMoroccoDoctors ? 'Réserver une démo' : 'Get Started'}
        </button>

        {/* Mobile Menu Button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            setMobileMenuOpen(!mobileMenuOpen)
          }}
          className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white/5 transition-colors"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className="md:hidden absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden"
          style={{
            background: isMoroccoDoctors
              ? 'rgba(0, 0, 0, 0.98)'
              : 'linear-gradient(135deg, rgba(10, 28, 53, 0.98) 0%, rgba(19, 45, 76, 0.95) 100%)',
            border: isMoroccoDoctors
              ? '1px solid rgba(255, 255, 255, 0.2)'
              : '1px solid rgba(203, 161, 53, 0.3)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          }}
        >
          <div className="py-2">
            {offerItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className="w-full px-4 py-3 text-left font-medium transition-all duration-200 hover:bg-white/5"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => {
                setMobileMenuOpen(false)
                if (isMoroccoDoctors) {
                  const schedulerElement = document.getElementById('scheduler')
                  if (schedulerElement) {
                    const yOffset = -100
                    const y = schedulerElement.getBoundingClientRect().top + window.pageYOffset + yOffset
                    window.scrollTo({ top: y, behavior: 'smooth' })
                  }
                } else {
                  navigate('/discover#scheduler')
                }
              }}
              className="w-full mx-2 mt-2 mb-2 px-4 py-3 rounded-lg font-semibold transition-all duration-300"
              style={{
                background: isMoroccoDoctors ? '#ffffff' : 'linear-gradient(135deg, #CBA135 0%, #E6C670 100%)',
                color: isMoroccoDoctors ? '#000000' : '#0A1C35',
                fontFamily: 'Playfair Display, Georgia, "Times New Roman", serif',
                fontWeight: 600,
                fontSize: '14px',
                boxShadow: isMoroccoDoctors
                  ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                  : '0 4px 20px rgba(203, 161, 53, 0.5)',
              }}
            >
              {isMoroccoDoctors ? 'Réserver une démo' : 'Get Started'}
            </button>
          </div>
        </div>
      )}
    </nav>
  )
}
